import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Typography,
} from '@mui/material';
import {
  fetchAttempt,
  fetchAudioBlobUrl,
  finishAttempt,
  submitAnswer,
} from '../mishtameretPortalApi';
import { examsPath } from '../nav';
import GemaraOrderCard from '../components/GemaraOrderCard';
import GemaraOrderNotice from '../components/GemaraOrderNotice';

/** הקלטת שאלה ממבחן ישן (שאלת בונוס שהוקראה בטלפון ולא הופיעה בדף) */
const QuestionAudioButton = ({ path, theme }) => {
  const [state, setState] = useState('idle'); // idle | loading | playing | error
  const audioRef = useRef(null);
  const urlRef = useRef('');

  useEffect(() => () => {
    audioRef.current?.pause();
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
  }, []);

  const toggle = async () => {
    if (state === 'playing') {
      audioRef.current?.pause();
      setState('idle');
      return;
    }
    try {
      if (!audioRef.current) {
        setState('loading');
        urlRef.current = await fetchAudioBlobUrl(path);
        audioRef.current = new Audio(urlRef.current);
        audioRef.current.onended = () => setState('idle');
      }
      await audioRef.current.play();
      setState('playing');
    } catch {
      setState('error');
    }
  };

  return (
    <Button
      type="button"
      onClick={toggle}
      disabled={state === 'loading'}
      sx={{
        mt: 0.75,
        minHeight: 32,
        px: 1.5,
        borderRadius: 999,
        border: `1px solid ${theme.border}`,
        color: state === 'error' ? theme.danger : theme.accent,
        fontWeight: 600,
        fontSize: '0.8rem',
      }}
    >
      {state === 'loading' ? <CircularProgress size={14} sx={{ color: theme.accent }} />
        : state === 'playing' ? '⏸ עצירה'
          : state === 'error' ? 'לא ניתן להשמיע — נסו שוב'
            : '▶ השמעת השאלה'}
    </Button>
  );
};

function sectionBadge(q) {
  if (!q?.sectionKey || q.sectionKey === 'main') return null;
  const label = q.sectionLabel || q.sectionKey;
  const pts = q.points
    ? ` +${Number.isInteger(q.points) ? q.points : Number(q.points).toFixed(2)}%`
    : '';
  return `${label}${pts}`;
}

function optionLabel(q, key) {
  const text = q?.options?.[key];
  if (text) return text;
  return String(key);
}

const TakeExamScreen = () => {
  const { theme, isStation = false, basePath = '/mishtameret' } = useOutletContext();
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingKey, setSavingKey] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const scoreRef = useRef(null);
  const justFinishedRef = useRef(false);

  // כפתור הסיום בתחתית העמוד — מיד בסיום מביאים את הנבחן אל הציון
  useEffect(() => {
    if (!justFinishedRef.current || attempt?.status !== 'completed') return;
    justFinishedRef.current = false;
    scoreRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [attempt?.status]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAttempt(attemptId);
        if (cancelled) return;
        setAttempt(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'שגיאה בטעינת המבחן');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attemptId]);

  const goBack = () => {
    navigate(examsPath(attempt?.exam?.frame, isStation));
  };

  const handleSelect = async (q, key) => {
    if (!q || attempt?.status !== 'in_progress') return;
    if (q.chosenDisplayKey === key) return;
    setSavingKey(`${q.globalIndex}:${key}`);
    setError('');
    try {
      const data = await submitAnswer(attemptId, q.globalIndex, key);
      setAttempt(data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת התשובה');
    } finally {
      setSavingKey(null);
    }
  };

  const handleFinish = async () => {
    setFinishing(true);
    setError('');
    try {
      const data = await finishAttempt(attemptId);
      justFinishedRef.current = true;
      setAttempt(data);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בסיום המבחן');
    } finally {
      setFinishing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress sx={{ color: theme.accent }} />
      </Box>
    );
  }

  if (!attempt) {
    return (
      <Box>
        <Button onClick={() => navigate(examsPath(undefined, isStation))} sx={{ mb: 2, color: theme.accent, fontWeight: 700 }}>
          ← חזור
        </Button>
        <Typography sx={{ color: theme.danger }}>{error || 'ניסיון לא נמצא'}</Typography>
      </Box>
    );
  }

  const questions = attempt.questions || [];
  const completed = attempt.status === 'completed';
  const answeredCount = attempt.answeredCount || 0;
  const totalQuestions = attempt.totalQuestions || questions.length;
  const allAnswered = totalQuestions > 0 && answeredCount >= totalQuestions;

  const renderScore = (ref) => (
    <Box
      ref={ref}
      sx={{
        borderRadius: 2,
        px: 2,
        py: 1.25,
        bgcolor: theme.mode === 'light' ? 'rgba(168,134,74,0.08)' : 'rgba(212,175,122,0.1)',
        border: `1px solid ${theme.borderSoft}`,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}
    >
      <Typography sx={{ fontWeight: 700, color: theme.text }}>הציון שלך</Typography>
      <Typography
        sx={{
          fontFamily: theme.fontDisplay,
          fontWeight: 800,
          fontSize: '2rem',
          color: theme.accent,
          lineHeight: 1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {attempt.score}
      </Typography>
    </Box>
  );

  const renderOptionButton = (q, key) => {
    const chosen = q.chosenDisplayKey === key;
    const reveal = completed;
    const isCorrectKey = reveal && q.correctDisplayKey === key;
    const isWrongChosen = reveal && chosen && q.isCorrect === false;
    const dimOthers = reveal && !isCorrectKey && !isWrongChosen;

    let border = theme.border;
    let bgcolor = theme.surface;
    let color = theme.text;
    let numberBg = 'transparent';
    let numberBorder = theme.border;
    let numberColor = theme.textMuted;
    let fontWeight = 500;

    if (!reveal && chosen) {
      border = theme.accent;
      bgcolor = theme.surfaceHover;
      color = theme.text;
      numberBg = theme.accent;
      numberBorder = theme.accent;
      numberColor = theme.accentContrast;
      fontWeight = 700;
    }

    if (isCorrectKey) {
      border = theme.success;
      bgcolor = theme.mode === 'light' ? 'rgba(46,125,50,0.08)' : 'rgba(129,199,132,0.12)';
      color = theme.success;
      numberBg = theme.success;
      numberBorder = theme.success;
      numberColor = '#fff';
      fontWeight = 700;
    }

    if (isWrongChosen) {
      border = theme.danger;
      bgcolor = theme.mode === 'light' ? 'rgba(198,40,40,0.08)' : 'rgba(229,115,115,0.12)';
      color = theme.danger;
      numberBg = theme.danger;
      numberBorder = theme.danger;
      numberColor = '#fff';
      fontWeight = 600;
    }

    if (dimOthers) {
      bgcolor = theme.surface;
      border = theme.borderSoft;
      color = theme.textMuted;
    }

    const busy = savingKey === `${q.globalIndex}:${key}`;

    return (
      <Button
        key={key}
        type="button"
        disabled={completed || Boolean(savingKey)}
        onClick={() => handleSelect(q, key)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          p: 1.5,
          borderRadius: 2,
          border: `2px solid ${border}`,
          bgcolor,
          color,
          textAlign: 'right',
          justifyContent: 'flex-start',
          textTransform: 'none',
          opacity: dimOthers ? 0.5 : 1,
          cursor: completed ? 'default' : 'pointer',
          transition: 'all 0.12s',
          '&:hover': completed
            ? { bgcolor }
            : { bgcolor: theme.surfaceHover, borderColor: theme.accent },
          '&.Mui-disabled': {
            bgcolor,
            color,
            border: `2px solid ${border}`,
            opacity: dimOthers ? 0.5 : 1,
          },
        }}
      >
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: '50%',
            border: `2px solid ${numberBorder}`,
            bgcolor: numberBg,
            color: numberColor,
            display: 'grid',
            placeItems: 'center',
            fontSize: 11,
            fontWeight: 800,
            shrink: 0,
            flexShrink: 0,
          }}
        >
          {busy ? <CircularProgress size={12} sx={{ color: numberColor }} /> : key}
        </Box>
        <Typography
          component="span"
          sx={{
            flex: 1,
            fontSize: '0.95rem',
            lineHeight: 1.35,
            fontWeight,
            color: 'inherit',
          }}
        >
          {optionLabel(q, key)}
        </Typography>
        {isCorrectKey ? (
          <Typography component="span" sx={{ color: theme.success, fontWeight: 800, fontSize: '1rem' }}>✓</Typography>
        ) : null}
        {isWrongChosen ? (
          <Typography component="span" sx={{ color: theme.danger, fontWeight: 800, fontSize: '1rem' }}>✗</Typography>
        ) : null}
      </Button>
    );
  };

  const renderQuestionCard = (q) => {
    const reveal = completed;
    const isRight = reveal && q.isCorrect === true;
    const isWrong = reveal && q.isCorrect === false;
    const cardBorder = isRight
      ? theme.success
      : isWrong
        ? theme.danger
        : theme.border;
    const cardBg = isRight
      ? (theme.mode === 'light' ? 'rgba(46,125,50,0.06)' : 'rgba(129,199,132,0.1)')
      : isWrong
        ? (theme.mode === 'light' ? 'rgba(198,40,40,0.06)' : 'rgba(229,115,115,0.1)')
        : theme.surface;
    const badge = sectionBadge(q);
    const markColor = isRight ? theme.success : isWrong ? theme.danger : theme.accent;

    return (
      <Box
        key={q.globalIndex}
        sx={{
          borderRadius: 2.5,
          border: `2px solid ${cardBorder}`,
          bgcolor: cardBg,
          p: 2,
          boxShadow: theme.shadow,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
              fontWeight: 800,
              fontSize: 12,
              bgcolor: markColor,
              color: (isRight || isWrong) ? '#fff' : theme.accentContrast,
            }}
          >
            {isRight ? '✓' : isWrong ? '✗' : (q.questionNumber ?? q.globalIndex + 1)}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.4, color: theme.text }}>
              {q.text || `שאלה ${q.questionNumber ?? q.globalIndex + 1}`}
            </Typography>
            {q.questionAudio ? <QuestionAudioButton path={q.questionAudio} theme={theme} /> : null}
            {badge ? (
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 10,
                    px: 1,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: theme.mode === 'light' ? 'rgba(123,31,162,0.08)' : 'rgba(206,147,216,0.15)',
                    color: theme.mode === 'light' ? '#7b1fa2' : '#ce93d8',
                    fontWeight: 600,
                  }}
                >
                  {badge}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 1, mr: { xs: 0, sm: 4.5 } }}>
          {(q.availableDisplayKeys || ['1', '2', '3']).map((key) => renderOptionButton(q, key))}
        </Box>
      </Box>
    );
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.borderSoft}`,
        }}
      >
        <Button
          onClick={goBack}
          aria-label="חזור"
          sx={{
            minHeight: 40,
            px: 1.5,
            borderRadius: 2,
            color: theme.accent,
            fontWeight: 700,
            '&:hover': { bgcolor: theme.surfaceHover },
          }}
        >
          ← חזור
        </Button>
        <Typography
          sx={{
            flex: 1,
            textAlign: 'center',
            fontWeight: 700,
            color: theme.accent,
            fontSize: '1rem',
            px: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {attempt.exam?.title}
          {attempt.exam?.frameLabel ? ` — ${attempt.exam.frameLabel}` : ''}
        </Typography>
        <Box sx={{ width: 72 }} />
      </Box>

      {error ? <Typography sx={{ color: theme.danger, mb: 2 }}>{error}</Typography> : null}

      <Box
        sx={{
          bgcolor: theme.surface,
          borderRadius: 2.5,
          border: `1px solid ${theme.borderSoft}`,
          px: 2,
          py: 1.5,
          mb: 2,
          boxShadow: theme.shadow,
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: theme.text }}>
          {attempt.exam?.title}
        </Typography>
        {attempt.exam?.materialLabel ? (
          <Typography sx={{ color: theme.textMuted, fontSize: '0.8rem', mt: 0.25 }}>
            {attempt.exam.materialLabel}
          </Typography>
        ) : null}

        {completed ? (
          <Box sx={{ mt: 1.5 }}>{renderScore(scoreRef)}</Box>
        ) : (
          <Typography sx={{ color: theme.textMuted, fontSize: '0.85rem', mt: 1 }}>
            נענו {answeredCount} מתוך {totalQuestions}
          </Typography>
        )}
      </Box>

      {completed
        ? <GemaraOrderCard attemptId={attemptId} theme={theme} />
        : <GemaraOrderNotice attemptId={attemptId} frame={attempt.exam?.frame} theme={theme} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 3 }}>
        {questions.map(renderQuestionCard)}
      </Box>

      {!completed ? (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', pb: 2 }}>
          <Button
            onClick={handleFinish}
            disabled={finishing || answeredCount === 0}
            sx={{
              minHeight: 48,
              px: 3,
              borderRadius: 999,
              bgcolor: theme.accent,
              color: theme.accentContrast,
              fontWeight: 700,
              '&:hover': { bgcolor: theme.accentSoft },
              '&.Mui-disabled': { bgcolor: theme.borderSoft, color: theme.textMuted },
            }}
          >
            {finishing
              ? <CircularProgress size={22} />
              : (allAnswered ? 'סיום מבחן וחישוב ציון' : 'סיום מוקדם')}
          </Button>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', pb: 2 }}>
          <Box sx={{ width: '100%' }}>{renderScore()}</Box>
          <Button
            onClick={() => navigate(`${basePath}/grades`)}
            sx={{
              minHeight: 44,
              px: 3,
              borderRadius: 999,
              bgcolor: theme.accent,
              color: theme.accentContrast,
              fontWeight: 700,
              '&:hover': { bgcolor: theme.accentSoft },
            }}
          >
            המבחנים שלי
          </Button>
          <Button
            onClick={goBack}
            sx={{
              minHeight: 44,
              px: 3,
              borderRadius: 999,
              border: `1px solid ${theme.border}`,
              color: theme.text,
              fontWeight: 600,
            }}
          >
            חזרה למבחנים
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TakeExamScreen;
